<!-- RemoteSelect.vue -->
<script setup lang="ts">
import { ref, reactive, onMounted, watch, PropType } from 'vue'
import { debounce } from 'lodash-es'
import { ElAvatar } from 'element-plus'
import { AvatarWithName } from '@/components/AvatarWithName'

const props = defineProps({
  modelValue: {
    type: [String, Number, Array] as PropType<string | number | (string | number)[]>,
    default: null
  },
  api: {
    type: Function as PropType<(params: any) => Promise<any>>,
    required: true
  },
  onOptionsLoaded: {
    type: Function as PropType<(options: any[]) => void>,
    default: () => {}
  },
  searchKey: {
    type: String,
    default: 'keyword'
  },
  labelField: {
    type: String,
    default: 'label'
  },
  valueField: {
    type: String,
    default: 'value'
  },
  formatLabel: {
    type: Function as PropType<(item: any) => string>,
    default: undefined
  },
  multiple: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请选择'
  },
  echoItems: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  // 新增 prop
  valueType: {
    type: String,
    default: 'id',
    validator: (val: string) => ['id', 'object'].includes(val)
  },
  // 额外的 API 参数（会合并到请求参数中）
  extraParams: {
    type: Object as PropType<Record<string, any>>,
    default: () => ({})
  },
  // 是否在下拉选项中展示头像（例如打手选择）
  showAvatar: {
    type: Boolean,
    default: false
  },
  // 头像字段名
  avatarField: {
    type: String,
    default: 'avatar'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

// 规范化值的类型
const normalizeValue = (value: any): any => {
  if (props.multiple) {
    // 多选模式：确保返回数组
    if (!Array.isArray(value)) {
      return []
    }
    return value.map((v) => {
      if (props.valueField === 'value' || props.valueField === 'id') {
        const numVal = Number(v)
        return isNaN(numVal) ? v : numVal
      }
      return v
    })
  } else {
    // 单选模式
    if (value !== null && value !== undefined) {
      if (props.valueField === 'value' || props.valueField === 'id') {
        const numVal = Number(value)
        return isNaN(numVal) ? value : numVal
      }
    }
    return value
  }
}

// 内部值（用于 v-model）
const innerValue = ref<any>(props.multiple ? [] : null)

// 监听 props.modelValue 变化，同步到内部值
watch(
  () => props.modelValue,
  (newVal) => {
    const normalized = normalizeValue(newVal)
    // 简单比较，避免不必要的更新
    if (props.multiple) {
      if (JSON.stringify(innerValue.value) !== JSON.stringify(normalized)) {
        innerValue.value = normalized
      }
    } else {
      if (innerValue.value !== normalized) {
        innerValue.value = normalized
      }
    }
  },
  { immediate: true }
)

// 处理选择变化
const handleChange = (value: any) => {
  const normalized = normalizeValue(value)
  emit('update:modelValue', normalized)

  // 抛出 change 事件，带上完整的选中项对象（不仅仅是 id）
  // 供父组件乐观更新使用
  if (props.multiple) {
    if (Array.isArray(normalized)) {
      // 从 options 中查找
      // 注意：selectedItemMap 可能还没包含最新的（如果是刚搜索出来的）
      // 所以优先从 options 找，找不到再找 selectedItemMap
      const items = normalized
        .map((val) => {
          const optionItem = options.value.find((opt) => opt[props.valueField] === val)
          return optionItem || selectedItemMap.value[val]
        })
        .filter(Boolean)
      emit('change', items)
    } else {
      emit('change', [])
    }
  } else {
    const item =
      options.value.find((opt) => opt[props.valueField] === normalized) ||
      selectedItemMap.value[normalized]
    emit('change', item)
  }
}

// ✅ 新增：用于根据 value 查找完整对象（供父组件使用）
const selectedItemMap = ref<Record<string | number, any>>({})

const loading = ref(false)
const options = ref<any[]>([]) // 存原始 item 对象
const searchQuery = ref('')
const pagination = reactive({ page: 1, limit: 20, total: 0 })
const hasMore = ref(true)

const syncOptions = () => {
  props.onOptionsLoaded(options.value)
}

// 加载选项
const loadOptions = async (reset = false) => {
  if (reset) {
    pagination.page = 1
    // options.value = [] // 不要立即清空，避免回显闪烁
    hasMore.value = true
  }

  loading.value = true
  try {
    const params: any = {
      page: pagination.page,
      limit: pagination.limit,
      ...props.extraParams // 合并额外参数
    }
    if (searchQuery.value) {
      params[props.searchKey] = searchQuery.value
    }

    const res = await props.api(params)
    const newOptions = res.data || []

    // ✅ 构建 value -> item 的映射
    newOptions.forEach((item) => {
      // 确保 value 是 number 类型（如果是 id）
      const value = item[props.valueField]
      if (value !== null && value !== undefined) {
        // 如果 valueField 是 'value' 或 'id'，尝试转换为 number
        if (props.valueField === 'value' || props.valueField === 'id') {
          const numValue = Number(value)
          if (!isNaN(numValue)) {
            item[props.valueField] = numValue
          }
        }
        selectedItemMap.value[item[props.valueField]] = item
      }
    })

    if (reset) {
      options.value = newOptions
    } else {
      options.value.push(...newOptions)
    }

    // ✅ 每次加载完数据后，确保回显项依然存在
    preloadEchoItems()

    pagination.total = res.total || 0
    hasMore.value = options.value.length < pagination.total
  } catch (e) {
    console.error('加载选项失败:', e)
  } finally {
    loading.value = false
    syncOptions()
  }
}

// 搜索处理（带防抖）
const handleSearchDebounced = debounce((query: string) => {
  // 更新搜索关键词
  const trimmedQuery = query?.trim() || ''
  searchQuery.value = trimmedQuery

  // 执行查询（无论是否为空都会查询，空时查询默认数据）
  loadOptions(true)
}, 500) // 500ms 防抖延迟

// 搜索处理（立即更新输入框，延迟执行查询）
const handleSearch = (query: string) => {
  // 如果正在首次加载，不处理搜索（避免重复调用）
  if (isInitialLoading.value) {
    return
  }

  // 如果还没有加载过数据，且输入为空，不处理（让 handleVisibleChange 处理首次加载）
  if (!hasLoaded.value && (!query || query.trim() === '')) {
    return
  }

  const trimmedQuery = query?.trim() || ''

  // 如果输入为空，取消之前的防抖调用，立即重置
  if (!trimmedQuery) {
    // 取消防抖
    handleSearchDebounced.cancel()
    // 立即重置
    searchQuery.value = ''
    options.value = []
    loadOptions(true)
  } else {
    // 有输入，使用防抖查询
    handleSearchDebounced(trimmedQuery)
  }
}

// 滚动加载
const handleScrollBottom = () => {
  if (hasMore.value && !loading.value) {
    pagination.page += 1
    loadOptions()
  }
}

// 获取选项的值（确保类型正确）
const getOptionValue = (item: any) => {
  if (props.valueType === 'object') {
    return item
  }
  const value = item[props.valueField]
  // 确保 value 是 number 类型（如果是 id/value）
  if (props.valueField === 'value' || props.valueField === 'id') {
    const numValue = Number(value)
    return isNaN(numValue) ? value : numValue
  }
  return value
}

const getOptionLabel = (item: any) => {
  if (props.formatLabel) {
    return props.formatLabel(item)
  }
  const field = props.labelField || 'label'
  const value = item[field]
  if (value === null || value === undefined) {
    return ''
  }
  return value.toString()
}

// 预加载回显项
const preloadEchoItems = () => {
  if (!props.echoItems?.length) return

  props.echoItems.forEach((item) => {
    const key = item[props.valueField]
    if (!selectedItemMap.value[key]) {
      selectedItemMap.value[key] = item
    }
    const exists = options.value.some((opt) => opt[props.valueField] === key)
    if (!exists) {
      options.value.push(item)
    }
  })

  syncOptions()
}

// 是否已经加载过数据（用于避免重复加载）
const hasLoaded = ref(false)
// 是否正在首次加载（用于避免 handleSearch 在首次打开时触发）
const isInitialLoading = ref(false)

// 处理下拉框显示/隐藏
const handleVisibleChange = (visible: boolean) => {
  if (visible) {
    if (!hasLoaded.value) {
      // 第一次打开时才加载数据
      // 设置标志，防止 remote-method 同时触发
      isInitialLoading.value = true
      hasLoaded.value = true // 提前设置，避免 remote-method 触发
      loadOptions(true).finally(() => {
        isInitialLoading.value = false
      })
    }
  } else {
    // 关闭时清空搜索关键词
    searchQuery.value = ''
  }
}

onMounted(() => {
  // 如果有回显项，预加载
  if (props.echoItems?.length > 0) {
    preloadEchoItems()
    // 如果有回显项，也需要加载数据以确保选项可用
    if (!hasLoaded.value) {
      isInitialLoading.value = true
      loadOptions(true).finally(() => {
        isInitialLoading.value = false
        hasLoaded.value = true
      })
    }
  }
})

watch(
  () => props.echoItems,
  () => {
    preloadEchoItems()
  },
  { deep: true }
)

// 判断选项是否禁用（如果已选中，则不禁用，允许删除）
const isOptionDisabled = (item: any) => {
  if (item.disabled === true) {
    const value = getOptionValue(item)
    if (props.multiple) {
      if (Array.isArray(innerValue.value)) {
        return !innerValue.value.includes(value)
      }
    } else {
      return innerValue.value !== value
    }
    return true
  }
  return false
}

// ✅ 暴露方法：让父组件能根据当前 value 获取完整对象
defineExpose({
  getSelectedItem: () => {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.map((v) => selectedItemMap.value[v]).filter(Boolean)
    }
    return selectedItemMap.value[props.modelValue] || null
  },
  reload: () => loadOptions(true),
  clear: () => {
    options.value = []
    selectedItemMap.value = {}
    searchQuery.value = ''
    pagination.page = 1
    hasMore.value = true
  }
})
</script>

<template>
  <el-select
    v-model="innerValue"
    :placeholder="placeholder"
    :loading="loading"
    :multiple="multiple"
    filterable
    remote
    :remote-method="handleSearch"
    :reserve-keyword="false"
    @scroll-bottom="handleScrollBottom"
    @visible-change="handleVisibleChange"
    @change="handleChange"
    style="width: 100%; min-height: 40px"
  >
    <el-option
      v-for="item in options"
      :key="`option-${item[valueField]}`"
      :label="getOptionLabel(item)"
      :value="getOptionValue(item)"
      :disabled="isOptionDisabled(item)"
    >
      <div
        v-if="showAvatar"
        style="display: flex; align-items: center; max-width: 100%; overflow: hidden"
      >
        <AvatarWithName :avatar="item[avatarField]" :name="getOptionLabel(item)" :size="24" />
      </div>
      <span
        v-else
        style="
          display: inline-block;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: middle;
        "
      >
        {{ getOptionLabel(item) }}
      </span>
    </el-option>
  </el-select>
</template>
